import axios from "axios";
import { getAdminToken } from "../utils/adminAuth";

// Base URL: on non-localhost hosts always use same-origin (avoids localhost baked in at build time).
// Local dev uses REACT_APP_API_URL or localhost:8001.
export const getApiBaseURL = () => {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host !== "localhost" && host !== "127.0.0.1") {
      return `${window.location.origin}/api/v1`;
    }
  }
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  return "http://localhost:8001/api/v1";
};

// Create axios instance with base configuration
const api = axios.create({
  baseURL: getApiBaseURL(),
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Ensure every request uses current API base (production: bot.cuchd.in, local: localhost:8001)
api.interceptors.request.use(
  (config) => {
    config.baseURL = getApiBaseURL();
    const adminToken = getAdminToken();
    if (adminToken) {
      config.headers.Authorization = `Bearer ${adminToken}`;
    }
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error("API Request Error:", error);
    return Promise.reject(error);
  },
);

const MAX_RETRIES = 3;
const TRANSIENT_STATUS_CODES = [502, 503, 504];

// Response interceptor for error handling and transient retries
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  async (error) => {
    console.error("API Response Error:", error.response?.data || error.message);

    const config = error.config;
    const status = error.response?.status;

    if (!config || !status || !TRANSIENT_STATUS_CODES.includes(status)) {
      return Promise.reject(error);
    }

    if (!config.__retryCount) {
      config.__retryCount = 0;
    }

    if (config.__retryCount >= MAX_RETRIES) {
      return Promise.reject(error);
    }

    config.__retryCount += 1;

    const jitter = Math.floor(Math.random() * 500);
    const delay = Math.pow(2, config.__retryCount - 1) * 300 + jitter;

    await new Promise((resolve) => setTimeout(resolve, delay));

    return api(config);
  },
);

// API service functions
export const conversationAPI = {
  // Send message to conversation endpoint
  sendMessage: async (message, sessionId) => {
    const response = await api.post("/conversation", {
      message,
      session_id: sessionId,
    });
    return response.data;
  },

  // Reset conversation session
  resetSession: async (sessionId) => {
    const response = await api.post(`/conversation/reset/${sessionId}`);
    return response.data;
  },

  // Get session information
  getSessionInfo: async (sessionId) => {
    const response = await api.get(`/conversation/session/${sessionId}`);
    return response.data;
  },

  // Navigate back one step
  goBack: async (sessionId) => {
    const response = await api.post(`/conversation/back/${sessionId}`);
    return response.data;
  },
};

export const servicesAPI = {
  // Categories Management
  getCategories: async () => {
    const response = await api.get("/services/categories");
    return response.data;
  },

  createCategory: async (request) => {
    const response = await api.post("/services/categories", request);
    return response.data;
  },

  deleteCategory: async (categoryName) => {
    const response = await api.delete(`/services/categories/${categoryName}`);
    return response.data;
  },

  updateCategory: async (oldCategoryName, newCategoryName) => {
    const response = await api.put(`/services/categories/${oldCategoryName}`, {
      category_name: newCategoryName,
    });
    return response.data;
  },

  // Subcategories Management
  getSubcategories: async (params) => {
    const response = await api.get("/services/subcategories", { params });
    return response.data;
  },

  createSubcategory: async (request) => {
    const response = await api.post("/services/subcategories", request);
    return response.data;
  },

  updateSubcategory: async (
    currentPath,
    oldSubcategoryName,
    newSubcategoryName,
  ) => {
    const response = await api.put("/services/subcategories", {
      current_path: currentPath,
      old_subcategory_name: oldSubcategoryName,
      new_subcategory_name: newSubcategoryName,
    });
    return response.data;
  },

  deleteSubcategory: async (currentPath, subcategoryName) => {
    const response = await api.delete("/services/subcategories", {
      data: {
        current_path: currentPath,
        subcategory_name: subcategoryName,
      },
    });
    return response.data;
  },

  // Services Management
  getService: async (serviceId) => {
    const response = await api.get(`/services/services/${serviceId}`);
    return response.data;
  },

  listServices: async (params = {}) => {
    const response = await api.get("/services/services", { params });
    return response.data;
  },

  createService: async (serviceData) => {
    const response = await api.post("/services/services", serviceData);
    return response.data;
  },

  updateService: async (serviceId, serviceData) => {
    const response = await api.put(
      `/services/services/${serviceId}`,
      serviceData,
    );
    return response.data;
  },

  deleteService: async (serviceId) => {
    const response = await api.delete(`/services/services/${serviceId}`);
    return response.data;
  },

  // Bulk upload operations
  bulkUploadServices: async (formData) => {
    const response = await api.post("/services/bulk-upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  downloadTemplate: async () => {
    const response = await api.get("/services/download-template");
    return response.data;
  },

  // Download exported data as CSV
  downloadExport: async () => {
    const response = await api.get("/services/export-csv", {
      responseType: "blob",
    });
    return response;
  },

  // Recycle bin functions
  getDeletedServices: async () => {
    const response = await api.get("/services/recycle-bin");
    return response.data;
  },

  restoreService: async (serviceId) => {
    const response = await api.post(
      `/services/recycle-bin/${serviceId}/restore`,
    );
    return response.data;
  },

  permanentDeleteService: async (serviceId) => {
    const response = await api.delete(
      `/services/recycle-bin/${serviceId}/permanent`,
    );
    return response.data;
  },
};

export const healthAPI = {
  // Health check
  checkHealth: async () => {
    const response = await api.get("/health");
    return response.data;
  },

  // Simple health check
  simpleHealthCheck: async () => {
    const response = await api.get("/health/simple");
    return response.data;
  },
};

export const faqAPI = {
  // FAQ CRUD Operations
  listFAQs: async (params = {}) => {
    const response = await api.get("/faq", { params });
    return response.data;
  },

  getFAQ: async (faqId) => {
    const response = await api.get(`/faq/${faqId}`);
    return response.data;
  },

  createFAQ: async (faqData) => {
    const response = await api.post("/faq", faqData);
    return response.data;
  },

  updateFAQ: async (faqId, faqData) => {
    const response = await api.put(`/faq/${faqId}`, faqData);
    return response.data;
  },

  deleteFAQ: async (faqId) => {
    const response = await api.delete(`/faq/${faqId}`);
    return response.data;
  },

  // FAQ Utility Operations
  getFAQCategories: async () => {
    const response = await api.get("/faq/categories");
    return response.data;
  },

  createCategory: async (categoryData) => {
    const response = await api.post("/faq/categories", categoryData);
    return response.data;
  },

  updateCategory: async (oldCategoryName, newCategoryName) => {
    const response = await api.put(`/faq/categories/${oldCategoryName}`, {
      name: newCategoryName,
    });
    return response.data;
  },

  deleteCategory: async (categoryName) => {
    const response = await api.delete(`/faq/categories/${categoryName}`);
    return response.data;
  },

  searchFAQs: async (query, category = null, limit = 20) => {
    const params = { q: query, limit };
    if (category) params.category = category;
    const response = await api.get("/faq/search", { params });
    return response.data;
  },

  toggleFAQStatus: async (faqId) => {
    const response = await api.patch(`/faq/${faqId}/toggle`);
    return response.data;
  },

  // Bulk upload operation
  bulkUploadFAQs: async (formData) => {
    const response = await api.post("/faq/bulk-upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  // Download template
  downloadTemplate: async () => {
    const response = await api.get("/faq/download-template");
    return response.data;
  },

  // Recycle bin functions
  getDeletedFAQs: async () => {
    const response = await api.get("/faq/recycle-bin");
    return response.data;
  },

  restoreFAQ: async (faqId) => {
    const response = await api.post(`/faq/recycle-bin/${faqId}/restore`);
    return response.data;
  },

  permanentDeleteFAQ: async (faqId) => {
    const response = await api.delete(`/faq/recycle-bin/${faqId}/permanent`);
    return response.data;
  },

  // Hierarchical FAQ endpoints
  getFAQDomains: async () => {
    const response = await api.get("/faq/hierarchy/domains");
    return response.data;
  },

  getFAQSubCatHeads: async (domain = null) => {
    const params = domain ? { domain } : {};
    const response = await api.get("/faq/hierarchy/sub-cat-heads", { params });
    return response.data;
  },

  getFAQSubCategories: async (domain = null, subCatHead = null) => {
    const params = {};
    if (domain) params.domain = domain;
    if (subCatHead) params.sub_cat_head = subCatHead;
    const response = await api.get("/faq/hierarchy/sub-categories", { params });
    return response.data;
  },

  getFAQHierarchyTree: async () => {
    const response = await api.get("/faq/hierarchy/tree");
    return response.data;
  },
};

// Ticket creation and listing API
export const ticketAPI = {
  // Create ticket from FAQ navigation
  createTicket: async (ticketData) => {
    const response = await api.post("/conversation/tickets/create", ticketData);
    return response.data;
  },
  // List all tickets (sessions with pending/confirmed ticket)
  listTickets: async () => {
    const response = await api.get("/tickets");
    return response.data;
  },
};

// Conversation logs API (admin / security audit)
export const logsAPI = {
  getConversationLogs: async (params = {}) => {
    const response = await api.get("/conversation/logs", { params });
    return response.data;
  },
};

// Utility function to generate session ID
export const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Error handling utility
export const handleAPIError = (error) => {
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    switch (status) {
      case 400:
        return `Bad Request: ${data.detail || "Invalid request data"}`;
      case 401:
        return "Unauthorized: Please log in again";
      case 403:
        return "Forbidden: You do not have permission to perform this action";
      case 404:
        return "Not Found: The requested resource was not found";
      case 422:
        return `Validation Error: ${data.detail || "Invalid input data"}`;
      case 500:
        return "Internal Server Error: Please try again later";
      default:
        return `Error ${status}: ${data.detail || "An unexpected error occurred"}`;
    }
  } else if (error.request) {
    // Network error
    return "Network Error: Unable to connect to the server";
  } else {
    // Other error
    return `Error: ${error.message}`;
  }
};

export default api;
